import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_PRICE_LIST } from "../../../graphql/queries/CompanyAdminContext";
import { UPDATE_PRICELIST } from "../../../graphql/mutations";
import PricelistRowTable from "./PricelistRowTable";
import SimpleModalCompact from "../../common/ModalCompact/SimpleModalCompact";
import { FormattedMessage, useIntl } from "react-intl";

export default function PricelistEditModal({ open, onClose, articlePriceListId, companyId, onSaved }) {

  const intl = useIntl();
  const isNew = !articlePriceListId || articlePriceListId === 0;

  const [model, setModel] = useState({
    articlePriceListId: 0,
    companyId,
    name: "",
    description: "",
    rows: [],
  });

  const [loadPriceList] = useLazyQuery(GET_PRICE_LIST, {
    variables: { articlePriceListId },
    onCompleted: (d) => {
      const pl = d?.companyAdminContext?.pricelists?.getPriceList;
      if (pl) setModel(pl);
    },
  });

  const [saveMutation] = useMutation(UPDATE_PRICELIST);

  useEffect(() => {
    if (!isNew && open) loadPriceList();
    if (isNew && open)
      setModel({ articlePriceListId: 0, companyId, name: "", description: "", rows: [] });
  }, [open]);

  const updateField = (field, value) => setModel({ ...model, [field]: value });
  const updateRow = (index, field, value) => {
    const updated = [...model.rows];
    updated[index] = { ...updated[index], [field]: value };
    setModel({ ...model, rows: updated });
  };

  const addRow = () => {
    setModel({
      ...model,
      rows: [
        ...model.rows,
        {
          articlePriceListRowId: 0,
          artNo: "",
          amount: 1,
          price: 0,
          productionCost: 0,
          shortDescription: "",
          sortIndex: model.rows.length,
        },
      ],
    });
  };

  const deleteRow = (index) => {
    const updated = model.rows.filter((r, i) => i !== index);
    setModel({ ...model, rows: updated });
  };

  const moveRow = ({ fromIndex, toIndex }) => {
    const updated = Array.from(model.rows);
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setModel({ ...model, rows: updated });
  };

  const save = async () => {
    // Create a copy of model.rows without sortIndex

    const rowsToSave = model.rows.map(({ sortIndex, ...rest }) => rest);

    const { data } = await saveMutation({
      variables: {
        model: { ...model, companyId, rows: rowsToSave }
      }
    });

    const success = data?.companyAdminContext?.pricelists?.updatePricelist?.success;
    if (success) {
      onSaved?.();
      onClose();
    } else {
      const errorMsg = intl.formatMessage({ id: "common.genericErrorMessage" });
      alert(errorMsg);
    }
  };


  const tableContainer = {
    overflowX: "auto", // keeps it responsive
    padding: "0 16px", // add horizontal padding
  };


  return (

    <SimpleModalCompact open={open} onClose={onClose} fullScreen title={
      isNew
        ? intl.formatMessage({ id: "admin.pricelists.modal.new" })
        : intl.formatMessage({ id: "admin.pricelists.modal.edit" })
    }

      footer={<><Button onClick={onClose}><FormattedMessage id="btn.cancel" /></Button>
        <Button variant="contained" onClick={save}><FormattedMessage id="btn.save" /></Button></>}

    >

      <Box sx={{ p: 1 }}>
        <Stack spacing={2} sx={{ mb: 4 }} direction="row">
          <TextField label={intl.formatMessage({ id: "common.name" })} value={model.name} onChange={(e) => updateField("name", e.target.value)} fullWidth />
          <TextField
            label={intl.formatMessage({ id: "common.description" })}
            value={model.description}
            onChange={(e) => updateField("description", e.target.value)}
            fullWidth
          />
        </Stack>

        <Button startIcon={<AddIcon />} onClick={addRow} sx={{ mb: 1 }}>
          <FormattedMessage id="admin.pricelists.rows.add" />
        </Button>
        <Box sx={tableContainer}>
          <PricelistRowTable
            rows={model.rows}
            onAddRow={addRow}
            onDeleteRow={deleteRow}
            onChangeRow={updateRow}
            onMoveRow={moveRow}
            companyId={companyId}
          />
        </Box>
      </Box>
    </SimpleModalCompact>
  );
}